import { t } from "../i18n";

// Cada validador devolve a mensagem de erro, ou `null` se o valor for válido.
// Campo vazio é `null`: obrigatoriedade é decidida pela tela, não aqui.

const onlyDigits = (value: string) => value.replace(/\D/g, "");

/** Limite de upload no cliente (PDF e imagens); o backend continua validando. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** CPF com os dois dígitos verificadores (aceita com ou sem máscara). */
export function cpfError(cpf: string): string | null {
  const digits = onlyDigits(cpf);
  if (!digits) return null;
  if (digits.length !== 11) return t("validation.cpfIncomplete");
  // "111.111.111-11" passa no cálculo dos dígitos, mas não existe.
  if (/^(\d)\1+$/.test(digits)) return t("validation.cpfInvalid");

  const checkDigit = (length: number) => {
    const sum = [...digits.slice(0, length)].reduce((acc, d, i) => acc + Number(d) * (length + 1 - i), 0);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]) ? null : t("validation.cpfInvalid");
}

// Siglas dos 27 CRMs regionais.
const UFS = new Set("AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO".split(" "));
// Número e UF em qualquer ordem, com prefixo "CRM" opcional: "123456/SP", "SP 123456", "CRM-SP 123456".
const CRM_PATTERN = /^(?:CRM[\s/-]*)?(?:(\d{1,7})[\s/-]*([A-Z]{2})|([A-Z]{2})[\s/-]*(\d{1,7}))$/;

/** CRM com número (até 7 dígitos) e UF válida. */
export function crmError(crm: string): string | null {
  const value = crm.trim().toUpperCase().replace(/\./g, "");
  if (!value) return null;
  const match = CRM_PATTERN.exec(value);
  if (!match) return t("validation.crmFormat");
  return UFS.has(match[2] ?? match[3]) ? null : t("validation.crmUf");
}

/** CRM enquanto digita: só dígitos, letras, espaço, "/" e "-", em maiúsculas. */
export function formatCrmInput(value: string): string {
  return value.toUpperCase().replace(/[^0-9A-Z\s/-]/g, "");
}

const todayIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

/** Data de nascimento ("aaaa-mm-dd"): não pode ser futura nem anterior a 1900. */
export function birthDateError(iso: string): string | null {
  if (!iso) return null;
  if (iso > todayIso()) return t("validation.birthFuture");
  return iso < "1900-01-01" ? t("validation.birthTooOld") : null;
}

/** Data desejada ("aaaa-mm-ddThh:mm", hora local): precisa estar no futuro. */
export function futureDateError(isoLocal: string): string | null {
  if (!isoLocal) return null;
  return new Date(isoLocal).getTime() > Date.now() ? null : t("validation.dateInPast");
}

/** Validade da carteirinha ("aaaa-mm"): vale até o fim do mês informado. */
export function cardValidityError(yearMonth: string): string | null {
  if (!yearMonth) return null;
  return yearMonth < todayIso().slice(0, 7) ? t("validation.cardExpired") : null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailError(email: string): string | null {
  const value = email.trim();
  if (!value) return null;
  return EMAIL_PATTERN.test(value) ? null : t("validation.emailInvalid");
}

// DDDs oficiais da Anatel (o intervalo 11-99 tem buracos: 20, 23, 30, 40...).
const VALID_DDDS = new Set(
  "11 12 13 14 15 16 17 18 19 21 22 24 27 28 31 32 33 34 35 37 38 41 42 43 44 45 46 47 48 49 51 53 54 55 61 62 63 64 65 66 67 68 69 71 73 74 75 77 79 81 82 83 84 85 86 87 88 89 91 92 93 94 95 96 97 98 99".split(" ")
);

/** Telefone BR com DDD (celular ou fixo), com ou sem o DDI 55. Vazio é erro: só o WhatsApp usa. */
export function phoneError(phone: string): string | null {
  const digits = onlyDigits(phone);
  // Acima de 11 dígitos, os 2 primeiros são o DDI 55 (número colado completo).
  const local = digits.length > 11 ? digits.slice(2) : digits;
  if (local.length < 2) return t("validation.phoneMissing");
  if (!VALID_DDDS.has(local.slice(0, 2))) return t("validation.phoneInvalidDdd");

  // Celular: 9 dígitos começando em 9. Fixo: 8 dígitos, sem 9/0/1 na frente.
  const number = local.slice(2);
  const isMobile = number.length === 9 && number.startsWith("9");
  const isLandline = number.length === 8 && !"901".includes(number[0]);
  return isMobile || isLandline ? null : t("validation.phoneIncomplete");
}
